import { isFocused } from "../focus/gate";
import { insideCorridors, insideRect } from "../maze/geometry";
import { applyHunt, type HuntState, resetHunt } from "../maze/hunt";
import { CURSOR_RADIUS, LEVELS } from "../maze/levels";
import type { FocusJudgment, GameEvent, Rect, Vec2 } from "../types";

export type GamePhase = "idle" | "ready" | "playing" | "scare" | "won";

export interface GameState {
  levelIndex: number;
  phase: GamePhase;
  /** Current, hunt-mutated corridor rects for the active level. */
  corridors: Rect[];
  hunt: HuntState;
  started: boolean;
  levelStartedAt: number;
  fails: number;
  /** Cursor position from the previous 'playing' tick (or the entry point into 'playing'),
   * used to sample the prev->cur path for tunnelling-proof wall detection. Null/undefined
   * outside 'playing', immediately after a restart/level change, or when a caller building
   * a GameState by hand (e.g. a client-side mirror of `enterLevel`) omits it — treated the
   * same as null: the next playing tick falls back to a single-point check at the cursor. */
  prevCursor?: Vec2 | null;
}

export interface StepInput {
  cursor: Vec2 | null;
  dtS: number;
  judgment: FocusJudgment | null;
  now: number;
  /** Overrides isFocused's default threshold (FOCUS_THRESHOLD_DEFAULT, src/focus/gate.ts)
   * for BOTH gating checks this tick: the hunt-pinch focusP and the wall-hit scare arm. */
  focusThreshold?: number;
}

export interface StepResult {
  state: GameState;
  events: GameEvent[];
}

function cloneRects(rects: Rect[]): Rect[] {
  return rects.map((r) => ({ ...r }));
}

export function createGame(): GameState {
  const level = LEVELS[0]!;
  return {
    levelIndex: 0,
    phase: "idle",
    corridors: cloneRects(level.corridors),
    hunt: resetHunt(level),
    started: false,
    levelStartedAt: 0,
    fails: 0,
    prevCursor: null,
  };
}

function enterLevel(levelIndex: number, now: number, fails: number): GameState {
  const level = LEVELS[levelIndex]!;
  return {
    levelIndex,
    phase: "ready",
    corridors: cloneRects(level.corridors),
    hunt: resetHunt(level),
    started: true,
    levelStartedAt: now,
    fails,
    prevCursor: null,
  };
}

type SweepHit = "goal" | "wall" | null;

/**
 * Samples the centre-to-centre path from `prev` to `cur` at steps no larger than
 * `radius` (always including the endpoint, in path order) so a cursor jump can't
 * tunnel through a wall — or skip clean over a goal band — between ticks. For each
 * sample, in order: 'goal' fires the instant a sample lands inside `goal`, checked
 * BEFORE that same sample's wall test; only if the sample misses the goal does
 * '!insideCorridors' get to fire 'wall'. Returns null when every sample clears both.
 */
function sweepPath(prev: Vec2, cur: Vec2, corridors: Rect[], goal: Rect, radius: number): SweepHit {
  const dx = cur.x - prev.x;
  const dy = cur.y - prev.y;
  const dist = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(dist / radius));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const p = { x: prev.x + dx * t, y: prev.y + dy * t };
    if (insideRect(p, goal)) return "goal";
    if (!insideCorridors(p, corridors, radius)) return "wall";
  }
  return null;
}

/**
 * Pure state transition. Phase machine:
 *   idle    -> ready                                  (first tick, starts the level clock)
 *   ready   -> playing                                 (cursor enters level.start)
 *   playing -> playing                                 (cursor inside corridors, hunt applied)
 *   playing -> ready   + 'wall','restart'               (cursor leaves corridors, unfocused)
 *   playing -> scare   + 'wall','scare'                 (cursor leaves corridors, focused)
 *   playing -> ready   + 'goal'                         (cursor enters goal, more levels remain)
 *   playing -> won     + 'goal','win'                   (cursor enters goal, last level)
 *   scare, won are terminal from stepGame's perspective (no further transitions).
 * `cursor === null` (no input yet) never moves the game or triggers a fail.
 */
export function stepGame(state: GameState, input: StepInput): StepResult {
  const events: GameEvent[] = [];

  if (state.phase === "idle") {
    return { state: enterLevel(state.levelIndex, input.now, state.fails), events };
  }

  if (state.phase === "scare" || state.phase === "won") {
    return { state, events };
  }

  const level = LEVELS[state.levelIndex]!;

  if (state.phase === "ready") {
    if (input.cursor !== null && insideRect(input.cursor, level.start)) {
      return { state: { ...state, phase: "playing", prevCursor: input.cursor }, events };
    }
    return { state, events };
  }

  // state.phase === "playing"
  if (input.cursor === null) {
    return { state, events };
  }

  const judgment = input.judgment;
  const focusP =
    judgment !== null && isFocused(judgment, input.now, { threshold: input.focusThreshold })
      ? judgment.p
      : 0;
  const { corridors, hunt } = applyHunt(
    level,
    state.corridors,
    input.cursor,
    focusP,
    input.dtS,
    state.hunt,
  );

  const prevCursor = state.prevCursor ?? input.cursor;
  const sweep = sweepPath(prevCursor, input.cursor, corridors, level.goal, CURSOR_RADIUS);

  if (sweep === "goal") {
    events.push("goal");
    const nextLevelIndex = state.levelIndex + 1;
    if (nextLevelIndex >= LEVELS.length) {
      events.push("win");
      const won = { ...state, phase: "won" as const, corridors, hunt, prevCursor: input.cursor };
      return { state: won, events };
    }
    return { state: enterLevel(nextLevelIndex, input.now, state.fails), events };
  }

  if (sweep === "wall") {
    events.push("wall");
    const nextState = { ...state, corridors, hunt, prevCursor: input.cursor };
    // Operator decision: level 3's wall hit ALWAYS scares — unconditional, not gated on
    // the focus judge (even a null judgment scares). Levels 1 and 2 keep the existing
    // focus-gated behaviour below.
    const shouldScare =
      level.id === 3 || isFocused(input.judgment, input.now, { threshold: input.focusThreshold });
    if (shouldScare) {
      events.push("scare");
      return { state: { ...nextState, phase: "scare" }, events };
    }
    events.push("restart");
    return { state: enterLevel(state.levelIndex, input.now, state.fails + 1), events };
  }

  return { state: { ...state, corridors, hunt, prevCursor: input.cursor }, events };
}
